import React, { useEffect, useMemo } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";

interface RunHistoryFilters {
  channel: string;
  actionType: string;
  audienceType: string;
  executionDateFrom: string;
  executionDateTo: string;
}

interface RunHistoryFilterProps {
  channel: string;
  actionType: string;
  audienceType: string;
  executionDateFrom: string;
  executionDateTo: string;
  channelList: Array<{ label: string; value: string }>;
  actionTypeList: Array<{ label: string; value: string }>;
  audienceTypeList: Array<{ label: string; value: string }>;
  onApplyFilters: (filters: RunHistoryFilters) => void;
  onResetFilters: () => void;
}

export const RunHistoryFilter: React.FC<RunHistoryFilterProps> = ({
  channel,
  actionType,
  audienceType,
  executionDateFrom,
  executionDateTo,
  channelList,
  actionTypeList,
  audienceTypeList,
  onApplyFilters,
  onResetFilters,
}) => {
  // Transform lists to options format
  const channelOptions = useMemo(
    () =>
      channelList?.map((item) => ({
        value: item.value,
        label: item.label,
      })) || [],
    [channelList]
  );

  const actionTypeOptions = useMemo(
    () =>
      actionTypeList?.map((item) => ({
        value: item.value,
        label: item.label,
      })) || [],
    [actionTypeList]
  );

  const audienceTypeOptions = useMemo(
    () =>
      audienceTypeList?.map((item) => ({
        value: item.value,
        label: item.label,
      })) || [],
    [audienceTypeList]
  );

  // Define filter fields
  const filterFields: FormField[] = useMemo(
    () => [
      {
        name: "executionDateFrom",
        label: "Execution Date From",
        type: "datepicker",
        placeholder: "Select Start Date",
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
      {
        name: "executionDateTo",
        label: "Execution Date To",
        type: "datepicker",
        placeholder: "Select End Date",
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
      {
        name: "channel",
        label: "Channel",
        type: "select",
        placeholder: "Select Channel",
        options: channelOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
      {
        name: "actionType",
        label: "Action Type",
        type: "select",
        placeholder: "Select Action Type",
        options: actionTypeOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
      {
        name: "audienceType",
        label: "Audience Type",
        type: "select",
        placeholder: "Select Audience Type",
        options: audienceTypeOptions,
        className: "col-span-1",
        classes: {
          container: "w-full mb-6",
        },
      },
    ],
    [channelOptions, actionTypeOptions, audienceTypeOptions]
  );

  // Create form instance
  const form = FormService.createForm({
    fields: filterFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Sync form values when URL params change
  useEffect(() => {
    form.reset({
      executionDateFrom: decodeURIComponent(executionDateFrom) || "",
      executionDateTo: decodeURIComponent(executionDateTo) || "",
      channel: decodeURIComponent(channel) || "",
      actionType: decodeURIComponent(actionType) || "",
      audienceType: decodeURIComponent(audienceType) || "",
    });
  }, [
    executionDateFrom,
    executionDateTo,
    channel,
    actionType,
    audienceType,
    form,
  ]);

  const handleFilterSubmit = () => {
    const formValues = form.getValues();
    onApplyFilters({
      executionDateFrom: formValues.executionDateFrom || "",
      executionDateTo: formValues.executionDateTo || "",
      channel: formValues.channel || "",
      actionType: formValues.actionType || "",
      audienceType: formValues.audienceType || "",
    });
  };

  const handleFilterReset = () => {
    form.reset({
      executionDateFrom: "",
      executionDateTo: "",
      channel: "",
      actionType: "",
      audienceType: "",
    });
    onResetFilters();
  };

  const handleFieldChange = (field: string, value: string) => {
    // Add any field-specific logic here if needed
  };

  return (
    <div>
      <DynamicForm
        fields={filterFields}
        form={form}
        onSubmit={handleFilterSubmit}
        onReset={handleFilterReset}
        onFieldChange={handleFieldChange}
        gridColumns={3}
        config={{
          layout: "vertical",
          showSubmitButton: true,
          showResetButton: true,
          submitButtonText: "Apply Filters",
          resetButtonText: "Clear",
          classes: {
            buttonContainer: "flex justify-end gap-4",
          },
        }}
      />
    </div>
  );
};
